import * as React from 'react';
import { AutoScrollbars, OverlayFormInput, OverlayFormTextArea, Button } from 'components';
import { actions as appActions } from 'services/app/app-service';
import { Captcha } from 'components/captcha';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { getOperationSafe, getFavorites, getLayerObject, getOmjsLayerConfig } from 'selectors';
import { IState } from 'services/root-reducer';
import { EOperationStatus } from 'types';
import { useTranslation } from 'react-i18next';
import { ObjectsHelper } from 'utils';
import { validateEmail } from 'utils/validation';
import { IOperationsState } from 'services/operations/operations-reducer';
import { FormContentFavoritesDetalization, getNameColumn } from './favorites-detalization';

export interface IFormPayloadFavoriteObject {
    object_id: number;
    layer_code: string;
    object_name: string;
    object_url: string;
}

export interface SubmitApplicationFormPayload {
    fio: string;
    organisation: string;
    phone: string;
    email: string;
    area: string;
    requirements: string;
    captcha_value: string;
    captcha_key: string;
    security_code: string;
    objects: string;
}

export interface ISubmitApplicationFormContentProps {
    onSubmit: (payload: SubmitApplicationFormPayload) => void;
}

export const SubmitApplicationFormContent = (props: ISubmitApplicationFormContentProps) => {
    const [ fio_field, setFioField ] = React.useState<string>('');
    const [ organisation_field, setOrganisationField ] = React.useState<string>('');
    const [ phone_field, setPhoneField ] = React.useState<string>('');
    const [ email_field, setEmailField ] = React.useState<string>('');
    const [ area_field, setAreaField ] = React.useState<string>('');
    const [ requirements_field, setRequirementsField ] = React.useState<string>('');
    const [ captcha_value, setCaptchaValue ] = React.useState<string>('');

    const [ fio_field_error, setFioFieldError ] = React.useState<string>('');
    const [ organisation_field_error, setOrganisationFieldError ] = React.useState<string>('');
    const [ phone_field_error, setPhoneFieldError ] = React.useState<string>('');
    const [ email_field_error, setEmailFieldError ] = React.useState<string>('');
    const [ area_field_error, setAreaFieldError ] = React.useState<string>('');
    const [ requirements_field_error, setRequirementsFieldError ] = React.useState<string>('');
    const [ captcha_value_error, setCaptchaValueError ] = React.useState<string>('');

    const favorites = useSelector(getFavorites);
    const captchaOperation: IOperationsState['CAPTCHA'] = useSelector(
        createSelector(
            (state: IState) => getOperationSafe(state, 'CAPTCHA'),
            operation => operation
        )
    );
    const { t } = useTranslation();
    const submitApplicationOperation = useSelector(
        (state: IState) => getOperationSafe(state, 'SUBMIT_APPLICATION_OVERLAY')
    );
    const submitApplicationForm = useSelector(
        (state: IState) => getOperationSafe(state, 'SUBMIT_APPLICATION_OVERLAY_FORM')
    );

    /* componentDidMount */
    React.useEffect(() => {
        appActions.loadNewCaptcha();
    }, []);

    React.useEffect(() => {
        if (submitApplicationForm.status === EOperationStatus.FAIL) {
            setCaptchaValue('');
            setCaptchaValueError(t('Неправильная капча'));
        }
    }, [ submitApplicationForm.status ]);

    const transformFavoritesIntoPayload = ():string => {
        let payload: IFormPayloadFavoriteObject[] = [];

        for (let [ layerCode, objectIds ] of Object.entries(favorites)) {
            for (let objectId of objectIds) {
                let object = getLayerObject(false, layerCode, objectId);
                let name = object.get(getNameColumn(layerCode));
                payload.push({
                    layer_code: layerCode,
                    object_id: objectId,
                    object_name: name,
                    object_url: ObjectsHelper.getObjectLink({
                        layer_code: layerCode,
                        object_id: objectId
                    })
                });
            }
        }

        return JSON.stringify(payload);
    };

    const validateForm = () => {
        let hasErrors = false;
        if (fio_field.length === 0) {
            setFioFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setFioFieldError('');
        }
        if (organisation_field.length === 0) {
            setOrganisationFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setOrganisationFieldError('');
        }
        if (phone_field.length === 0) {
            setPhoneFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setPhoneFieldError('');
        }
        if (email_field.length === 0) {
            setEmailFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setEmailFieldError('');
        }
        if (!validateEmail(email_field)) {
            setEmailFieldError(t('Email указан неверно'));
            hasErrors = true;
        } else {
            setEmailFieldError('');
        }
        if (area_field.length === 0) {
            setAreaFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setAreaFieldError('');
        }
        if (requirements_field.length === 0) {
            setRequirementsFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setRequirementsFieldError('');
        }
        if (captcha_value.length === 0) {
            setCaptchaValueError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setCaptchaValueError('');
        }

        return hasErrors;
    };

    const onSendButtonClick = () => {
        let hasErrors = validateForm();

        if (!hasErrors) {
            props.onSubmit({
                fio: fio_field,
                organisation: organisation_field,
                phone: phone_field,
                email: email_field,
                area: area_field,
                requirements: requirements_field,
                objects: transformFavoritesIntoPayload(),
                security_code: __SECURITY_CODE__,

                captcha_key: captchaOperation.data.CAPTCHA_KEY,
                captcha_value
            });
        }
    };


    return (
        <AutoScrollbars className='padding-fix'>
            <div className="form-header">
                {t('Подать заявку')}
            </div>
            <div className="form-body">
                <OverlayFormInput
                    required
                    error={fio_field_error}
                    placeholder={t('ФИО')}
                    wrapperClassname='submit-application-input'
                    value={fio_field}
                    onChange={setFioField}
                />

                <OverlayFormInput
                    required
                    error={organisation_field_error}
                    placeholder={t('Организация')}
                    wrapperClassname='submit-application-input'
                    value={organisation_field}
                    onChange={setOrganisationField}
                />

                <OverlayFormInput
                    required
                    error={phone_field_error}
                    placeholder={t('Телефон')}
                    wrapperClassname='submit-application-input'
                    value={phone_field}
                    onChange={setPhoneField}
                />

                <OverlayFormInput
                    required
                    error={email_field_error}
                    placeholder={t('Email')}
                    wrapperClassname='submit-application-input'
                    value={email_field}
                    onChange={setEmailField}
                />

                <OverlayFormInput
                    required
                    error={area_field_error}
                    placeholder={t('Площадь земельного участка')}
                    wrapperClassname='submit-application-input'
                    value={area_field}
                    onChange={setAreaField}
                />

                <OverlayFormTextArea
                    required
                    error={requirements_field_error}
                    placeholder={t('Требования к площадке')}
                    wrapperClassname='submit-application-input'
                    value={requirements_field}
                    onChange={setRequirementsField}
                />
            </div>

            <FormContentFavoritesDetalization />

            <div className="form-footer">
                <div className='captcha-top-label'>{t('Введите слово с картинки')}</div>
                <div className="footer-actions-wrapper">
                    <Captcha
                        captchaOperation={captchaOperation}
                        renderInput={() => (
                            <OverlayFormInput
                                hideErrorMessage
                                error={captcha_value_error}
                                variant='underlined'
                                value={captcha_value}
                                placeholder={t('Введите слово')}
                                onChange={setCaptchaValue}
                            />
                        )}
                    />

                    <Button
                        disabled={captchaOperation.status !== EOperationStatus.SUCCESS}
                        className='submit-application-submit-button'
                        onClick={onSendButtonClick}
                        text={t('Отправить')}
                    />
                </div>
            </div>

            <div
                className="license-agreement"
                dangerouslySetInnerHTML={{
                    __html: t('LICENSE_AGREEMENT')
                }}
            />
        </AutoScrollbars>
    );
};
